import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { TOP_LEVEL_CATEGORIES } from '../../data/categoryInfo';
import { useSeo } from '../../hooks/useSeo';

/** Pagina istituzionale "I Nostri Prodotti", mostrata a TUTTI gli utenti su
 * `/i-nostri-prodotti` (indirizzo dedicato, separato dal catalogo/ricerca
 * reale che vive su `/prodotti/:categoryId` — richiesta utente 15/02/2026,
 * per rendere la ricerca prodotti fruibile anche ai non loggati). Ogni card
 * è cliccabile e porta alla pagina di dettaglio della categoria
 * (`/i-nostri-prodotti/:categorySlug`, vedi `CategoryInfoPage.tsx`).
 * Nota: la card "Ktek" è stata rimossa da qui su richiesta utente
 * (21/02/2026) — il marchio resta accessibile solo dal menù di navigazione
 * ("Ktek" → `/ktek`), non più duplicato in questa panoramica categorie. */
export function GuestCategoriesOverview(): React.ReactElement {
  useSeo(
    'Toner, gruppi completi e ricambi per stampanti | DTK',
    'Scopri toner, cartucce laser, gruppi completi, ricambi e attrezzature tecniche per stampanti, fotocopiatrici e multifunzione professionali.'
  );

  return (
    <div data-testid="guest-categories-overview">
      <div className="border-b bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <span>/</span>
          <span className="text-foreground font-medium">Prodotti</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8 max-w-2xl">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">I Nostri Prodotti</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            DTK offre un assortimento completo di consumabili, gruppi completi, ricambi e attrezzature tecniche per stampanti, fotocopiatrici e sistemi multifunzione dei principali marchi presenti sul mercato.
            Accedi con il tuo account B2B per consultare il catalogo completo, la disponibilità in tempo reale e i tuoi prezzi riservati.
          </p>
        </div>

        <div className="rounded-lg border bg-white shadow-sm divide-y sm:divide-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4" data-testid="guest-categories-grid">
          {TOP_LEVEL_CATEGORIES.map((cat, i) => (
            <Link
              to={`/i-nostri-prodotti/${cat.slug}`}
              key={cat.slug}
              className={`group p-6 flex flex-col hover:bg-secondary/40 transition-colors ${i > 0 ? 'sm:border-l' : ''}`}
              data-testid={`guest-category-${cat.slug}`}
            >
              <h2 className="text-base font-semibold mb-4 text-[#2B5EA7] group-hover:underline">{cat.cardTitle}</h2>
              <div className="rounded-md overflow-hidden mb-4 aspect-[4/3] bg-secondary">
                <img src={cat.image} alt={cat.cardTitle} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">{cat.cardDescription}</p>
              <span className="mt-auto flex items-center gap-1 text-xs font-semibold text-[#2B5EA7]">
                Scopri di più <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>

        {/* CTA registrazione */}
        <div className="mt-10 relative overflow-hidden rounded-lg bg-gradient-to-r from-[#1E4D8C] to-[#2B5EA7] p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 shadow-lg" data-testid="guest-categories-cta">
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-1.5">Sei un rivenditore?</h2>
            <p className="text-white/80 text-sm">
              Registrati per accedere al catalogo completo con prezzi riservati, disponibilità in tempo reale e ordini online.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to="/registrati"
              data-testid="guest-categories-register"
              className="h-11 px-6 rounded-md bg-gradient-to-r from-amber-300 to-amber-400 text-[#1E4D8C] text-sm font-bold flex items-center gap-2 hover:from-amber-400 hover:to-amber-500 hover:scale-[1.03] shadow-sm hover:shadow-md transition-all"
            >
              Registrati ora <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/accedi"
              data-testid="guest-categories-login"
              className="h-11 px-6 rounded-md border-2 border-white/50 text-white text-sm font-medium flex items-center hover:border-amber-400 hover:bg-amber-400 hover:text-[#1E4D8C] transition-colors"
            >
              Accedi
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
